/**
 * toc-highlight.js
 * スクロール位置に合わせて目次の現在位置にクラスを付与する
 *
 * 【仕様】
 * - .toc が存在しないページでは何もしない
 * - ビューポート上部のアクティブゾーンに入った見出しに対応する目次アイテムに activeClass を付与
 * - 複数の見出しが同時にゾーン内にある場合は最も上にあるものを採用
 * - h3 がアクティブの場合、親の h2 アイテムにも activeClass を付与
 *
 * 【デフォルト値】
 * - tocSelector  : '.toc'
 * - activeClass  : 'is-current'
 * - breakpoint   : 1024  （1024px 以上が desktop 扱い）
 * - offset.desktop : 100px（sticky の top 値に合わせる）
 * - offset.mobile  : 80px
 *
 * 【使い方】
 * // デフォルト設定のまま使う
 * initTocHighlight();
 *
 * // 一部だけ上書き（省略した値はデフォルトを使用）
 * initTocHighlight({ offset: { desktop: 120 } });
 *
 * // すべて指定
 * initTocHighlight({
 *   tocSelector : '.toc',
 *   activeClass : 'is-current',
 *   breakpoint  : 1200,
 *   offset      : { desktop: 120, mobile: 60 },
 * });
 */

const DEFAULTS = {
  tocSelector : '.toc',
  activeClass : 'is-current',
  breakpoint  : 1024,
  offset      : { desktop: 100, mobile: 80 },
};

export function initTocHighlight(options = {}) {
  const cfg = {
    ...DEFAULTS,
    ...options,
    offset: { ...DEFAULTS.offset, ...options.offset },
  };

  const toc = document.querySelector(cfg.tocSelector);
  if (!toc) return;

  const links = [...toc.querySelectorAll('a[href^="#"]')];
  const headings = links
    .map(a => document.getElementById(a.getAttribute('href').slice(1)))
    .filter(Boolean);

  if (!headings.length) return;

  const getOffset = () =>
    window.matchMedia(`(min-width: ${cfg.breakpoint}px)`).matches
      ? cfg.offset.desktop
      : cfg.offset.mobile;

  const active = new Set();
  let lastActive = null;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach(e => e.isIntersecting ? active.add(e.target) : active.delete(e.target));
      updateActive();
    },
    { rootMargin: `-${getOffset()}px 0px -60% 0px` }
  );

  headings.forEach(h => observer.observe(h));

  function updateActive() {
    toc.querySelectorAll(`.${cfg.activeClass}`)
       .forEach(el => el.classList.remove(cfg.activeClass));

    // アクティブゾーンに見出しがある場合はその中で最上位を採用し記憶する。
    // ない場合（見出しと見出しの間）は最後に記憶した見出しを維持する。
    let current;
    if (active.size) {
      current = [...active].sort(
        (a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top
      )[0];
      lastActive = current;
    } else {
      current = lastActive;
    }

    if (!current) return;

    const link = toc.querySelector(`a[href="#${current.id}"]`);
    if (!link) return;

    const item = link.closest('li');
    item.classList.add(cfg.activeClass);

    // h3 がアクティブな場合、親の toc__item も点灯
    const parentItem = item.closest('.toc__child-list')?.closest('li');
    if (parentItem) parentItem.classList.add(cfg.activeClass);
  }
}
